import React, { useState } from 'react';
import { Box, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { ConfirmDialog } from '../../reports/edit/EditCommon';
import type { MathColumn } from '../report/mathReportApi';
import { EMPTY_FIELD, hasErrors, validateField } from './fieldsCalc';
import type { FieldErrors } from './fieldsCalc';

interface FieldFormDialogProps {
  open: boolean;
  /** Редактируемое поле; null - добавление нового. Форма заполняется при монтировании: на каждое открытие свой key */
  target: MathColumn | null;
  fields: MathColumn[];
  saving: boolean;
  /** Возвращает true, если поле сохранено и форму можно закрыть */
  onSave: (draft: MathColumn, isEditing: boolean) => Promise<boolean>;
  onClose: () => void;
}

const FieldFormDialog: React.FC<FieldFormDialogProps> = ({ open, target, fields, saving, onSave, onClose }) => {
  const isEditing = target !== null;
  const [draft, setDraft] = useState<MathColumn>(() => (target ? { ...target } : EMPTY_FIELD));
  const [errors, setErrors] = useState<FieldErrors>({});

  const submit = async () => {
    if (saving) return;
    const found = validateField(draft, isEditing, fields);
    setErrors(found);
    if (hasErrors(found)) return;
    if (await onSave(draft, isEditing)) onClose();
  };

  // Enter в текстовом поле сохраняет форму, как кнопка
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Enter' && input.tagName === 'INPUT' && input.type === 'text') {
      event.preventDefault();
      submit();
    }
  };

  return (
    <ConfirmDialog
      open={open}
      title={isEditing ? 'Редактировать поле' : 'Добавить поле'}
      confirmText={isEditing ? 'Сохранить' : 'Добавить'}
      loading={saving}
      maxWidth="xs"
      onConfirm={submit}
      onClose={onClose}
    >
      <Box onKeyDown={handleKeyDown} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          size="small"
          label="Идентификатор поля"
          value={draft.field}
          onChange={(event) => {
            setDraft({ ...draft, field: event.target.value });
            setErrors({ ...errors, field: undefined });
          }}
          error={!!errors.field}
          helperText={errors.field || (isEditing ? 'Идентификатор изменить нельзя' : 'Имя колонки: латиница, цифры и знак _')}
          disabled={isEditing}
          autoFocus={!isEditing}
          required
        />
        <TextField
          size="small"
          label="Заголовок в таблице"
          value={draft.headerName}
          onChange={(event) => {
            setDraft({ ...draft, headerName: event.target.value });
            setErrors({ ...errors, headerName: undefined });
          }}
          error={!!errors.headerName}
          helperText={errors.headerName || 'Подпись колонки в мат. отчете'}
          autoFocus={isEditing}
          required
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={draft.editable}
              onChange={(event) => setDraft({ ...draft, editable: event.target.checked })}
            />
          }
          label="Разрешить редактирование значений в отчете"
        />
      </Box>
    </ConfirmDialog>
  );
};

export default FieldFormDialog;
